import React, { useState } from "react";
import {
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import {
  useGetPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
} from "../../../Api/platformApi";
import {
  AppPage,
  PageHeader,
  CardSurface,
  ActionButton,
  SlidePanel,
  ConfirmDialog,
  StatusBadge,
} from "../../../Components/ui";
import {
  NAVY,
  GOLD,
  GOLD_SOFT,
  TEXT_SECONDARY,
  BORDER,
  SUCCESS,
  ERROR,
} from "../../../theme/tokens";

const blank = {
  id: null,
  name: "",
  monthly_price: 0,
  yearly_price: 0,
  included_sms: 0,
  included_whatsapp: 0,
  included_numbers: 0,
  included_users: 0,
};

const Plans: React.FC = () => {
  const { data } = useGetPlansQuery();
  const [createPlan] = useCreatePlanMutation();
  const [updatePlan] = useUpdatePlanMutation();
  const [deletePlan] = useDeletePlanMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(blank);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const rows = data?.data ?? [];
  const num = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    const body = {
      ...form,
      monthly_price: Number(form.monthly_price),
      yearly_price: Number(form.yearly_price) || null,
      included_sms: Number(form.included_sms),
      included_whatsapp: Number(form.included_whatsapp),
      included_numbers: Number(form.included_numbers),
      included_users: Number(form.included_users),
    };
    if (form.id) await updatePlan({ id: form.id, body }).unwrap();
    else await createPlan(body).unwrap();
    setOpen(false);
  };

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<LayersRoundedIcon />}
          title="Subscription Plans"
          subtitle="Manage the plans available on the INTALK platform"
          actions={
            <ActionButton
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setForm(blank);
                setOpen(true);
              }}
            >
              Add Plan
            </ActionButton>
          }
        />
      }
    >
      <Grid container spacing={3}>
        {rows.map((p: any) => (
          <Grid item xs={12} md={4} key={p.id}>
            <CardSurface
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                height: "100%",
              }}
            >
              {/* Header row */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography fontSize={20} fontWeight={800} color={NAVY}>
                  {p.name}
                </Typography>
                <StatusBadge status={p.is_active ? "active" : "inactive"} />
              </Stack>

              {/* Pricing */}
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography fontSize={32} fontWeight={900} color={NAVY} lineHeight={1}>
                    ${Number(p.monthly_price).toFixed(2)}
                  </Typography>
                  <Typography fontSize={13} color={TEXT_SECONDARY}>/mo</Typography>
                </Stack>
                {p.yearly_price ? (
                  <Typography fontSize={12} color={TEXT_SECONDARY}>
                    ${Number(p.yearly_price).toFixed(2)}/yr
                  </Typography>
                ) : null}
              </Stack>

              {/* Quotas */}
              <Stack
                spacing={1}
                sx={{
                  p: 2,
                  borderRadius: "10px",
                  bgcolor: alpha(NAVY, 0.03),
                  border: `1px solid ${BORDER}`,
                }}
              >
                <QuotaItem icon={<SmsRoundedIcon />} label="SMS" value={p.included_sms} />
                <QuotaItem icon={<WhatsAppIcon />} label="WhatsApp" value={p.included_whatsapp} />
                <QuotaItem icon={<PhoneRoundedIcon />} label="Numbers" value={p.included_numbers} />
                <QuotaItem icon={<GroupRoundedIcon />} label="Users" value={p.included_users} />
              </Stack>

              {/* Features */}
              {(p.features_json ?? []).length > 0 && (
                <Stack spacing={0.75}>
                  {(p.features_json ?? []).map((f: string, i: number) => (
                    <Stack key={i} direction="row" alignItems="center" spacing={1}>
                      <CheckRoundedIcon sx={{ fontSize: 14, color: SUCCESS }} />
                      <Typography fontSize={13} color={TEXT_SECONDARY}>{f}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Admin Actions */}
              <Stack direction="row" spacing={1} mt="auto">
                <Button
                  size="small"
                  startIcon={<EditRoundedIcon />}
                  onClick={() => {
                    setForm({ ...p });
                    setOpen(true);
                  }}
                  sx={{
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha(NAVY, 0.2),
                  }}
                  variant="outlined"
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteRoundedIcon />}
                  onClick={() => setDeleteId(p.id)}
                  sx={{
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: ERROR,
                    borderColor: alpha(ERROR, 0.3),
                    "&:hover": { bgcolor: alpha(ERROR, 0.06) },
                  }}
                  variant="outlined"
                >
                  Delete
                </Button>
              </Stack>
            </CardSurface>
          </Grid>
        ))}

        {rows.length === 0 && (
          <Grid item xs={12}>
            <CardSurface
              noHover
              sx={{
                p: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                textAlign: "center",
              }}
            >
              <LayersRoundedIcon sx={{ fontSize: 48, color: alpha(NAVY, 0.2) }} />
              <Typography fontSize={16} fontWeight={700} color={NAVY}>
                No plans yet
              </Typography>
              <Typography fontSize={13} color={TEXT_SECONDARY}>
                Create your first subscription plan to offer it to companies.
              </Typography>
              <ActionButton
                startIcon={<AddRoundedIcon />}
                onClick={() => {
                  setForm(blank);
                  setOpen(true);
                }}
              >
                Add Plan
              </ActionButton>
            </CardSurface>
          </Grid>
        )}
      </Grid>

      {/* Create / Edit SlidePanel */}
      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title={form.id ? "Edit Plan" : "Add Plan"}
        subtitle={form.id ? `Editing: ${form.name}` : "Create a new subscription plan"}
        icon={<LayersRoundedIcon />}
        footer={
          <>
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              sx={{ borderRadius: "10px" }}
            >
              Cancel
            </Button>
            <ActionButton onClick={save} disabled={!form.name}>
              {form.id ? "Save Changes" : "Create Plan"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2.5}>
          <TextField
            label="Plan Name"
            value={form.name}
            onChange={num("name")}
            fullWidth
            required
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Monthly $"
              type="number"
              value={form.monthly_price}
              onChange={num("monthly_price")}
              fullWidth
            />
            <TextField
              label="Yearly $"
              type="number"
              value={form.yearly_price}
              onChange={num("yearly_price")}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Incl. SMS"
              type="number"
              value={form.included_sms}
              onChange={num("included_sms")}
              fullWidth
            />
            <TextField
              label="Incl. WhatsApp"
              type="number"
              value={form.included_whatsapp}
              onChange={num("included_whatsapp")}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Incl. Numbers"
              type="number"
              value={form.included_numbers}
              onChange={num("included_numbers")}
              fullWidth
            />
            <TextField
              label="Incl. Users"
              type="number"
              value={form.included_users}
              onChange={num("included_users")}
              fullWidth
            />
          </Stack>
        </Stack>
      </SlidePanel>

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteId !== null}
        title="Delete plan?"
        message="This will permanently remove this subscription plan. Existing subscribers may be affected."
        confirmText="Delete"
        danger
        onClose={() => setDeleteId(null)}
        onConfirm={async () => {
          if (deleteId !== null) await deletePlan(deleteId);
          setDeleteId(null);
        }}
      />
    </AppPage>
  );
};

const QuotaItem: React.FC<{ icon: React.ReactElement; label: string; value: number }> = ({
  icon,
  label,
  value,
}) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Stack direction="row" alignItems="center" spacing={1}>
      {React.cloneElement(icon, { sx: { fontSize: 14, color: TEXT_SECONDARY } })}
      <Typography fontSize={12} color={TEXT_SECONDARY}>
        {label}
      </Typography>
    </Stack>
    <Typography fontSize={12} fontWeight={700} color={NAVY}>
      {Number(value).toLocaleString()}
    </Typography>
  </Stack>
);

export default Plans;
